import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabase';

const SuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const [successStories, setSuccessStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuccessStories();
  }, []);

  const loadSuccessStories = async () => {
    try {
      // Fetch top collectibles with significant value gains from public collections
      const { data, error } = await supabase?.from('collectibles')?.select(`
          id,
          name,
          collectible_type,
          purchase_price,
          current_value,
          estimated_value,
          primary_image_url,
          created_at,
          user_profiles!collectibles_user_id_fkey(full_name, avatar_url),
          collections!collectibles_collection_id_fkey(is_public, name)
        `)?.not('purchase_price', 'is', null)?.not('current_value', 'is', null)?.gt('current_value', 0)?.gt('purchase_price', 0)?.limit(20);

      if (!error && data?.length > 0) {
        // Filter to public collections and calculate ROI
        const withROI = data?.filter(c => c?.collections?.is_public)?.map(c => {
            const purchase = parseFloat(c?.purchase_price) || 0;
            const current = parseFloat(c?.current_value || c?.estimated_value) || 0;
            const roi = purchase > 0 ? ((current - purchase) / purchase) * 100 : 0;
            return { ...c, roi, purchase, current };
          })?.filter(c => c?.roi > 0)?.sort((a, b) => b?.roi - a?.roi)?.slice(0, 3);

        if (withROI?.length > 0) {
          const mapped = withROI?.map(c => ({
            id: c?.id,
            collector: c?.user_profiles?.full_name || 'Collector',
            avatar: c?.user_profiles?.avatar_url || null,
            category: c?.collectible_type?.replace(/-/g, ' ')?.replace(/\b\w/g, ch => ch?.toUpperCase()) || 'Collectibles',
            timeline: `${Math.max(1, Math.round((Date.now() - new Date(c.created_at)?.getTime()) / (1000 * 60 * 60 * 24 * 30)))} months`,
            initialInvestment: `$${c?.purchase?.toLocaleString()}`,
            currentValue: `$${c?.current?.toLocaleString()}`,
            roi: `+${c?.roi?.toFixed(1)}%`,
            story: `A dedicated collector building an impressive ${c?.collectible_type?.replace(/-/g, ' ')} portfolio using Crazy Rare Club's market analysis and tracking tools.`,
            keyWin: `${c?.roi?.toFixed(0)}% return on ${c?.name}`,
            featured: {
              item: c?.name,
              image: c?.primary_image_url || null,
              bought: `$${c?.purchase?.toLocaleString()}`,
              current: `$${c?.current?.toLocaleString()}`,
              change: `+${c?.roi?.toFixed(1)}%`,
            },
          }));
          setSuccessStories(mapped);
          return;
        }
      }
      setSuccessStories([]);
    } catch (err) {
      console.error('Error loading success stories:', err);
      setSuccessStories([]);
    } finally {
      setLoading(false);
    }
  };

  const currentStory = successStories?.[activeStory];

  if (loading) {
    return (
      <section className="py-24 bg-gradient-to-b from-background to-muted/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">Success Stories</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 animate-pulse">
            <div className="space-y-4">
              <div className="h-20 bg-card border border-border rounded-xl" />
              <div className="h-32 bg-card border border-border rounded-xl" />
              <div className="h-24 bg-card border border-border rounded-xl" />
            </div>
            <div className="h-80 bg-card border border-border rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  if (successStories?.length === 0) {
    return (
      <section className="py-24 bg-gradient-to-b from-background to-muted/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">Success Stories</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Join our community and start building your own success story with professional-grade tools.
            </p>
          </motion.div>
          <div className="text-center py-16">
            <Icon name="Award" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Success stories will appear here as our community grows.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Success Stories
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Real collectors, real results. See how our platform has helped passionate collectors achieve remarkable returns while building collections they love.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Story Content */}
          <motion.div
            key={activeStory}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Collector Profile */}
            <div className="flex items-center space-x-6">
              <Image
                src={currentStory?.avatar}
                alt={currentStory?.collector}
                className="w-20 h-20 rounded-full object-cover"
              />
              <div>
                <h3 className="text-2xl font-bold text-foreground">
                  {currentStory?.collector}
                </h3>
                <p className="text-lg text-primary font-medium">
                  {currentStory?.category} Collector
                </p>
                <p className="text-muted-foreground">
                  {currentStory?.timeline} journey
                </p>
              </div>
            </div>

            {/* ROI Metrics */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-4 bg-card border border-border rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Initial</div>
                <div className="text-lg font-bold text-card-foreground">
                  {currentStory?.initialInvestment}
                </div>
              </div>
              <div className="text-center p-4 bg-card border border-border rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Current</div>
                <div className="text-lg font-bold text-primary">
                  {currentStory?.currentValue}
                </div>
              </div>
              <div className="text-center p-4 bg-success/10 border border-success/20 rounded-xl">
                <div className="text-sm text-success mb-1">ROI</div>
                <div className="text-lg font-bold text-success">
                  {currentStory?.roi}
                </div>
              </div>
            </div>

            {/* Story */}
            <div className="bg-card border border-border rounded-xl p-6">
              <blockquote className="text-muted-foreground leading-relaxed mb-4">
                "{currentStory?.story}"
              </blockquote>
              <div className="flex items-center space-x-2 text-primary">
                <Icon name="Award" size={16} />
                <span className="text-sm font-medium">Key Achievement:</span>
                <span className="text-sm">{currentStory?.keyWin}</span>
              </div>
            </div>

            {/* Featured Item */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-lg font-semibold text-card-foreground mb-4">
                Featured Success
              </h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Purchased</div>
                  <div className="font-bold text-card-foreground">
                    {currentStory?.featured?.bought}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Current Value</div>
                  <div className="font-bold text-primary">
                    {currentStory?.featured?.current}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Growth</div>
                  <div className="font-bold text-success">
                    {currentStory?.featured?.change}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Showcase */}
          <div className="space-y-6">
            {/* Featured Item Image */}
            <motion.div
              key={activeStory}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-2xl"
            >
              <Image
                src={currentStory?.featured?.image}
                alt={currentStory?.featured?.item}
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h4 className="text-xl font-semibold text-foreground mb-2">
                  {currentStory?.featured?.item}
                </h4>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Purchased: {currentStory?.featured?.bought}
                  </span>
                  <span className="text-success font-bold">
                    {currentStory?.featured?.change}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Story Navigation */}
            <div className="space-y-3">
              {successStories?.map((story, index) => (
                <button
                  key={story?.id}
                  onClick={() => setActiveStory(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 premium-hover ${
                    index === activeStory
                      ? 'bg-primary/10 border border-primary/20' :'bg-card border border-border hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Image
                        src={story?.avatar}
                        alt={story?.collector}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold text-card-foreground">
                          {story?.collector}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {story?.category} • {story?.timeline}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-success">
                        {story?.roi}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        ROI
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessStories;